/**
 * Web Bluetooth ESC/POS for 58mm thermal printers (browser / Chrome Android).
 * UUIDs and discovery pattern aligned with common BLE ESC/POS adapters (Epson, Microchip UART, Nordic nRF).
 */

const ESCPOS_SERVICE_UUIDS = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
  /* Many MPT-III / generic BLE-serial modules use FFE0 + FFE1 write */
  "0000ffe0-0000-1000-8000-00805f9b34fb",
];

const TX_CHAR_UUIDS = [
  "00002af1-0000-1000-8000-00805f9b34fb",
  "49535343-8841-43f4-a8d4-ecbe34729bb3",
  "6e400002-b5a3-f393-e0a9-e50e24dcca9e",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ffe2-0000-1000-8000-00805f9b34fb",
];

export const BLUETOOTH_REQUEST_OPTIONS = {
  acceptAllDevices: true,
  optionalServices: ESCPOS_SERVICE_UUIDS,
};

export function isWebBluetoothSupported() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.bluetooth &&
    typeof window !== "undefined" &&
    (window.isSecureContext === true ||
      window.location?.protocol === "https:" ||
      window.location?.hostname === "localhost")
  );
}

/**
 * @param {BluetoothRemoteGATTServer} server
 * @returns {Promise<{ service: BluetoothRemoteGATTService, characteristic: BluetoothRemoteGATTCharacteristic }>}
 */
export async function findPrinterCharacteristic(server) {
  for (const serviceUUID of ESCPOS_SERVICE_UUIDS) {
    try {
      const service = await server.getPrimaryService(serviceUUID);

      for (const charUUID of TX_CHAR_UUIDS) {
        try {
          const char = await service.getCharacteristic(charUUID);
          if (char.properties.write || char.properties.writeWithoutResponse) {
            return { service, characteristic: char };
          }
        } catch {
          /* try next characteristic */
        }
      }

      const chars = await service.getCharacteristics();
      for (const char of chars) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          return { service, characteristic: char };
        }
      }
    } catch {
      /* try next service */
    }
  }
  throw new Error("No writable ESC/POS characteristic found on this device.");
}

/**
 * BLE writes are MTU-limited; many devices need small chunks for writeWithoutResponse.
 * @param {BluetoothRemoteGATTCharacteristic} characteristic
 * @param {Uint8Array} data
 */
export async function writeEscPosInChunks(characteristic, data) {
  const useWoResponse = characteristic.properties.writeWithoutResponse;
  const chunkSize = useWoResponse ? 20 : 244;

  for (let offset = 0; offset < data.length; offset += chunkSize) {
    const chunk = data.slice(offset, offset + chunkSize);
    if (useWoResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
    } else {
      await characteristic.writeValueWithResponse(chunk);
    }
  }
}

export async function printEscPosToKnownDevice(device, escPosBytes) {
  if (!device?.gatt) {
    throw new Error("Invalid Bluetooth device.");
  }
  const server = device.gatt.connected
    ? device.gatt
    : await device.gatt.connect();
  const { characteristic } = await findPrinterCharacteristic(server);
  await writeEscPosInChunks(characteristic, escPosBytes);
}

/**
 * Opens device picker (user gesture), connects GATT, writes raw ESC/POS bytes.
 * @param {Uint8Array} escPosBytes
 * @param {object} [options]
 * @param {BluetoothRequestDeviceFilter[]} [options.filters]
 */
export async function printEscPosViaWebBluetooth(escPosBytes, options = {}) {
  if (!isWebBluetoothSupported()) {
    throw new Error(
      "Web Bluetooth is not available (use Chrome over HTTPS or localhost)."
    );
  }

  const requestOpts = {
    optionalServices: ESCPOS_SERVICE_UUIDS,
    ...(options.filters?.length
      ? { filters: options.filters }
      : { acceptAllDevices: true }),
  };

  const device = await navigator.bluetooth.requestDevice(requestOpts);
  await printEscPosToKnownDevice(device, escPosBytes);
}
